<template>
    <MenuBar>
        <template #start>
            <button
                v-if="!shouldHideSidebar"
                type="button"
                @click="toggleSideBar"
                @mouseenter="isHovering = ui.showSidebar ? false : true"
            >
                <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                    <path
                        fill="var(--text-color)"
                        fill-rule="evenodd"
                        d="M10 7h8a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-8V7ZM9 7H6a1 1 0 0 0-1 1v8a1 1 0 0 0 1 1h3V7ZM4 8a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V8Z"
                        clip-rule="evenodd"
                    ></path>
                </svg>
            </button>
            <RouterLink
                id="logo-route"
                :to="
                    stek && organization
                        ? {
                              name: 'stekPage',
                              params: {
                                  orgName: organization.name,
                                  stekName: stek.name,
                              },
                          }
                        : organization
                          ? {
                                name: 'organizationPage',
                                params: {
                                    orgName: organization.name,
                                },
                            }
                          : '/'
                "
            >
                <!-- Stek -->
                <img
                    v-if="stek?.identity && logoDataUrl"
                    :src="stek.logo.preview"
                    class="stek-logo"
                    alt="Stek Logo"
                    id="logo"
                />
                <h1 v-if="stek?.name">{{ stek.name }}</h1>
                <MyStekzLogo id="logo" v-else-if="!organization" />
                <h1 v-else>{{ organization.name }}</h1>
            </RouterLink>
            <Breadcrumb v-if="showBreadcrumbs" />
        </template>
        <template #end>
            <ActivityBar />
            <div class="toolbar-buttons-container">
                <SyncStatus />
                <EditViewButton v-if="isViewingLatestVersion && stek" />
                <SettingsMenu />
            </div>
        </template>
    </MenuBar>

    <div
        class="page-with-sidebar"
        :class="{
            'collapsed': ui.showSidebar === false ? true : false,
            'no-sidebar-mode': shouldHideSidebar,
        }"
    >
        <div
            ref="sidebar"
            id="sidebar"
            :class="{ 'hovering': isHovering }"
            v-if="!shouldHideSidebar"
        >
            <NavSideBar />
            <div
                :class="{
                    'resize-handle': true,
                    'resizing': isDragging,
                }"
                @mousedown.prevent="startResize"
            />
        </div>
        <div
            class="main-container"
            :class="{ 'show-history-sidebar': showHistory }"
        >
            <div class="page">
                <RouterView />
            </div>
            <div v-if="showHistory" class="history-sidebar">
                <ArtefactHistory />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { useMouse } from "@vueuse/core";

import { computed, nextTick, onMounted, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import ActivityBar from "@/components/ActivityBar.vue";
import ArtefactHistory from "@/components/ArtefactHistory.vue";
import Breadcrumb from "@/components/Breadcrumb.vue";
import EditViewButton from "@/components/EditViewButton.vue";
import NavSideBar from "@/components/NavSideBar.vue";
import SyncStatus from "@/components/SyncStatus.vue";
import { useEditEntityDialog } from "@/composables/edit-entity-dialog";
import { useEditRoleDialog } from "@/composables/edit-role-dialog";
import { useOrganizationManager } from "@/composables/organization-manager";
import { usePolicyAcceptance } from "@/composables/policy-acceptance";
import { useStekManager } from "@/composables/stek-manager";
import {
    AuthStatus,
    useAuthenticationStore,
} from "@/stores/authentication-store";
import { useHistoryManager } from "@/stores/history";
import { useUIStore } from "@/stores/ui-store";

import MyStekzLogo from "../assets/mystekz-logo.svg?component";
import SettingsMenu from "./menus/SettingsMenu.vue";

const route = useRoute();
const ui = useUIStore();
const { organization } = useOrganizationManager();
const { stek, logoDataUrl } = useStekManager();
const authStore = useAuthenticationStore();
const { checkAndShowPolicyDialog } = usePolicyAcceptance();

const { registerHotKey: registerRoleHotKey } = useEditRoleDialog();
const { registerHotKey: registerEntityHotKey } = useEditEntityDialog();

registerRoleHotKey();
registerEntityHotKey();

// Check for policy acceptance when user is authenticated - only on main app pages
watch(
    [() => authStore.authStatus, () => route.name],
    async ([newStatus, routeName]) => {
        if (newStatus === AuthStatus.SIGNED_IN) {
            // Only show policy dialog on main app pages, not on auth/public pages
            const mainAppRoutes = [
                "home",
                "organizationPage",
                "organizationHome",
                "stekPage",
                "domainPage",
                "productPage",
                "processPage",
                "profile",
                "settings",
                "application",
            ];

            if (routeName && mainAppRoutes.includes(routeName)) {
                // Small delay to ensure user data is fully loaded
                await nextTick();
                setTimeout(async () => {
                    try {
                        await checkAndShowPolicyDialog();
                    } catch (error) {
                        console.error(
                            "Error checking policy acceptance:",
                            error
                        );
                    }
                }, 500);
            }
        }
    },
    { immediate: true }
);

// Note, avoid importing 'showHistory' from the history manager. Somehow
// it will cause the route in the computed artefact in the history manager
// store to be undefined and unreactive.
const showHistory = computed(
    () => "version" in route.params && route.params.version
);

const { isViewingLatestVersion } = useHistoryManager();

const toggleSideBar = () => {
    ui.toggleSideBar();
};

const showBreadcrumbs = computed(() => "breadcrumbs" in route.meta);

const shouldHideSidebar = computed(() => {
    return route.meta.hideSidebar === true;
});

const sidebar = useTemplateRef<HTMLElement>("sidebar");

const isHovering = ref(false);
const isDragging = ref(false);
const { x } = useMouse();

const startResize = () => {
    isDragging.value = true;
    document.addEventListener("mousemove", handleResize);
    document.addEventListener("mouseup", stopResize);
};

const handleResize = (e: MouseEvent) => {
    if (isDragging.value && sidebar.value) {
        const newWidth = e.pageX;
        if (newWidth >= 120 && newWidth <= 500) {
            sidebar.value.style.width = `${newWidth}px`;
            sidebar.value.style.flexBasis = `${newWidth}px`;
            ui.sidebarWidth = newWidth;
        }
    }
};

const stopResize = () => {
    isDragging.value = false;
    document.removeEventListener("mousemove", handleResize);
    document.removeEventListener("mouseup", stopResize);
};

watch(x, () => {
    // if the sidebar is collapsed, make it visible (but still collapsed)
    if (x.value < 5 && !ui.showSidebar) {
        isHovering.value = true;
    }
    // Turn hovering off when mouse x leaves sidebar
    if (
        !isDragging.value &&
        isHovering.value &&
        x.value > ui.sidebarWidth + 10
    ) {
        isHovering.value = false;
    }
});

onMounted(() => {
    updateSidebarWidth();
});

// Watch for changes to the shouldHideSidebar value
watch(
    [shouldHideSidebar, sidebar],
    () => {
        if (!shouldHideSidebar.value && sidebar.value) {
            // Use a nextTick to ensure the sidebar element is in the DOM
            nextTick(() => {
                updateSidebarWidth();
            });
        }
    },
    { immediate: true }
);

const updateSidebarWidth = () => {
    if (sidebar.value) {
        sidebar.value.style.width = `${ui.sidebarWidth}px`;
        sidebar.value.style.flexBasis = `${ui.sidebarWidth}px`;
    }
};
</script>

<style scoped lang="scss">
.p-menubar {
    position: fixed;
    width: 100%;
    z-index: 1000;

    :deep(.p-menubar-start) {
        overflow-x: auto;
    }

    :deep(.p-menubar-end) {
        display: flex;
        gap: 0.5em;
    }
    height: var(--menu-bar-height);
}

a#logo-route {
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
    align-items: center;
    justify-content: center;
    padding-left: 8px;
    padding-right: 8px;
    text-decoration: none;
    text-decoration: inherit;

    h1 {
        margin: 0;
        color: var(--text-color);
        padding: 0;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    #logo {
        display: block;
        clear: none;
        height: var(--text-xxl);
        max-width: 16em;
    }
}

.page-with-sidebar {
    display: flex;
    flex-direction: row;

    // make room for the fixed menubar and presence bar on top
    #sidebar,
    .main-container {
        padding-top: var(--menu-bar-height);
        position: relative;
    }

    &.collapsed {
        #sidebar {
            z-index: 600;
            position: fixed;

            &:not(.hovering) {
                transform: translateX(-100%);
            }
        }
    }

    &.no-sidebar-mode {
        .main-container {
            padding-left: 0;
            margin-left: 0;
        }
    }

    #sidebar {
        height: 100vh;
        background: var(--p-menubar-background);
        border-right: 1px solid var(--p-menubar-border-color);
        display: flex;
        flex-direction: column;
        flex-grow: 0;
        flex-shrink: 0;
        left: 0;
        min-width: 120px;
        max-width: 500px;
        overflow-y: hidden; // note we need this for the default resize handle
        position: relative; /* Needed for absolute positioning of handle */
        transform: translateX(0);
        transition-property: transform;
        transition-timing-function: ease;
        transition-duration: 200ms;

        &.on-top {
            z-index: 600;
            overflow: visible;
        }
    }

    .main-container {
        width: 100%;
        height: 100vh;
        overflow-y: scroll;
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        &.show-history-sidebar {
            flex-direction: row;
        }

        .history-sidebar {
            display: flex;
            flex: 0 1 20rem;
            border-left: 1px solid var(--palette-grey-300);
            flex-direction: column;
        }
    }
}

.page {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    padding: calc(var(--page-margin) * 0.5) var(--page-margin);
}

.resize-handle {
    position: absolute;
    top: 0;
    right: 0;
    width: 3px;
    height: 100%;
    background: transparent;
    cursor: col-resize;
    transition: 100ms 50ms ease-in-out;

    &:hover,
    &.resizing {
        background: var(--stekz-primary-background);
    }
}

.popover-container {
    .info-text {
        padding: 1rem;
        text-align: center;
    }

    :deep(.p-datatable) {
        .sync-action-buttons {
            display: flex;
            gap: 8px;
        }
    }
}

.toolbar-buttons-container {
    display: flex;
    gap: 0.5em;
    padding-top: 1rem;
    padding-bottom: 1rem;
}
</style>
